import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { PageContext, ThemeContext, UserContext } from '../Context';
import GetUserAvatar from '../tool/GetUserAvatar';
import GetUserField from '../tool/GetUserField';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import { 
  Menu, 
  MenuItem, 
  Button, 
  Box, 
  Typography,
  Divider,
  Fade,
  Grow,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import ManageAccountIcon from '@mui/icons-material/ManageAccounts';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LogoutIcon from '@mui/icons-material/Logout';

function DropdownMenu() {
  const this_year = new Date().getFullYear();
  const { theme } = useContext(ThemeContext);
  const { user, setUser } = useContext(UserContext);
  const { page, setPage } = useContext(PageContext);
  const muiTheme = useTheme();
  const isCompact = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [matricule, setMatricule] = useState(null);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpenCgu = () => {
    sessionStorage.setItem('cguPreviousPage', page || 'Edition');
    setPage('CGU');
    handleClose();
  };
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      if (token) {
        await axios.post('/api/user/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Erreur lors du log de déconnexion:', error);
    } finally {
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('authExpiration');
      setUser(null);
      handleClose();
    }
  };

  const menuItems = [
    { label: 'Rédaction', icon: EditIcon, page: 'Edition', color: '#4CAF50' },
    { label: 'Mon compte', icon: ManageAccountIcon, page: 'Account', color: '#FF9800' },
    { label: 'Guide', icon: MenuBookIcon, page: 'Tutorial', color: '#9C27B0' },
    { label: 'Déconnexion', icon: LogoutIcon, action: handleLogout, color: '#F44336' }
  ];

  // Styles adaptatifs pour le thème
  const isDark = theme === 'dark';
  
  const buttonStyles = {
    bgcolor: isDark 
      ? 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)'
      : 'linear-gradient(135deg, #425f9e 0%, #667eea 100%)',
    background: isDark 
      ? 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)'
      : 'linear-gradient(135deg, #425f9e 0%, #667eea 100%)',
    color: 'white',
    borderRadius: '20px',
    px: { xs: 1, sm: 1.5, md: 2.5 },
    py: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    textTransform: 'none',
    fontWeight: 550,
    fontSize: '0.85rem',
    boxShadow: isDark 
      ? '0 3px 12px rgba(0, 0, 0, 0.4)'
      : '0 3px 12px rgba(102, 126, 234, 0.3)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      background: isDark 
        ? 'linear-gradient(135deg, #1a252f 0%, #2c3e50 100%)'
        : 'linear-gradient(135deg,#101b28 0%,rgb(65, 79, 153) 100%)',
      boxShadow: isDark 
        ? '0 5px 16px rgba(0, 0, 0, 0.6)'
        : '0 5px 16px rgba(102, 126, 234, 0.4)',
      transform: 'translateY(-1px)'
    },
    '&:active': {
      transform: 'translateY(0px)'
    }
  };

  const menuStyles = {
    width: 220,
    mt: 0.5,
    background: isDark 
      ? 'linear-gradient(145deg, #2c3e50 0%, #34495e 100%)'
      : 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
    boxShadow: isDark 
      ? '0 10px 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(52, 73, 94, 0.3)'
      : '0 10px 30px rgba(0, 0, 0, 0.15), 0 0 20px rgba(102, 126, 234, 0.1)',
    borderRadius: '15px',
    border: isDark 
      ? '1px solid rgba(52, 73, 94, 0.3)'
      : '1px solid rgba(102, 126, 234, 0.1)',
    overflow: 'hidden',
  };

  const footerStyles = {
    p: 2, 
    textAlign: 'center',
    borderTop: isDark 
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(102, 126, 234, 0.1)'
  };

  return (
    <Box className="dropdown">
      <GetUserField fieldName="matricule" onLoad={setMatricule} />
      <Button
        variant="contained"
        onClick={handleClick}
        endIcon={
          <ArrowDropDownIcon 
            sx={{ 
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease-in-out'
            }} 
          />
        }
        sx={buttonStyles}
      >
        {matricule && <GetUserAvatar matricule={matricule} defaultValue="..." compact={isCompact} />}
      </Button>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Grow}
        transitionDuration={300}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: menuStyles
        }}
      >
        {menuItems.map((item, index) => (
          <MenuItem 
            key={item.label}
            onClick={() => {
              if (item.action) {
                item.action();
              } else {
                setPage(item.page);
                handleClose();
              }
            }}
            sx={{
              py: 1.5,
              px: 2,
              transition: 'all 0.2s ease-in-out',
              borderRadius: '8px',
              mx: 1,
              my: 0.5,
              '&:hover': {
                bgcolor: isDark 
                  ? `${item.color}25`
                  : `${item.color}15`,
                transform: 'translateX(5px)',
                boxShadow: `0 2px 8px ${item.color}${isDark ? '40' : '20'}`,
                '& .MuiSvgIcon-root': {
                  color: item.color,
                  transform: 'scale(1.1)'
                }
              }
            }}
          >
            <item.icon 
              sx={{ 
                mr: 2, 
                fontSize: '1.2rem',
                color: isDark ? '#bbb' : '#666',
                transition: 'all 0.2s ease-in-out'
              }} 
            />
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500,
                color: isDark ? '#fff' : '#333'
              }}
            >
              {item.label}
            </Typography>
          </MenuItem>
        ))}
        
        <Box sx={footerStyles}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: isDark ? '#bbb' : '#666',
              fontWeight: 500,
              display: 'block',
              mb: 0.5
            }}
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleOpenCgu();
              }}
              style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 600,
                opacity: 0.85
              }}
            >
              CGU
            </span>
            {' · '}
            v<SettingsFieldDisplay fieldName="version" /> ©{this_year}
          </Typography>
          <Typography variant="caption" component="div">
            <a 
              href="https://pyll.be/bluenote" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                background: isDark 
                  ? 'linear-gradient(135deg,rgb(68, 167, 233),rgb(83, 139, 196))'
                  : 'linear-gradient(135deg, #667eea, #1a253c)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                transition: 'all 0.2s ease-in-out',
                display: 'inline-block',
                lineHeight: '1.6',
                padding: '2px 0',
                backgroundSize: '100% 100%'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              <i style={{ fontSize: '1.6em', lineHeight: 'inherit' }}>pyll</i> <b style={{ fontSize: '0.8em', lineHeight: 'inherit' }}>SRL</b>
            </a>
          </Typography>
        </Box>
      </Menu>
    </Box>
  );
}

export default DropdownMenu;